import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiService from "../Common/Configurations/APIService";
import config from "../Common/Configurations/APIConfig";
import currencyFlags from "../Data/CurrencyFlags.json";
import DefaultData from "../CompanyData/DefaultData";
import PricingSection from "../StaticPages/PricingSection";

const HeroFeature = ({ icon, title, text }) => (
  <div className="d-flex mb-4 align-items-start text-start">
    <div className="me-3 mt-1">
      <div
        className="rounded-circle d-flex align-items-center justify-content-center"
        style={{
          width: "40px",
          height: "40px",
          backgroundColor: "rgba(255,255,255,0.2)",
          border: "1px solid rgba(255,255,255,0.3)",
        }}
      >
        <i className={`${icon} text-white fs-5`}></i>
      </div>
    </div>
    <div>
      <h6 className="fw-bold mb-1 text-white fs-5">{title}</h6>
      <p className="mb-0 small fs-15" style={{ color: "rgba(255,255,255,0.7)" }}>
        {text}
      </p>
    </div>
  </div>
);

const CheckItem = ({ title, text }) => (
  <div className="d-flex mb-3 align-items-start text-start">
    <i className="ri-checkbox-circle-fill me-3 fs-4 text-success"></i>
    <div>
      <p className="mb-0 fs-6 text-dark">
        <strong>{title}:</strong> {text}
      </p>
    </div>
  </div>
);

const StepCard = ({ number, title, text }) => (
  <div className="col-lg-3 col-md-6 text-center mb-4">
    <div className="p-4 rounded-4 h-100 border shadow-sm bg-white">
      <div
        className="mb-3 mx-auto d-flex align-items-center justify-content-center rounded-circle"
        style={{
          width: "50px",
          height: "50px",
          backgroundColor: DefaultData.THEMECOLOR1,
          color: "#fff",
          fontSize: "1.25rem",
          fontWeight: "bold",
        }}
      >
        {number}
      </div>
      <h5 className="fw-bold">{title}</h5>
      <p className="small text-muted mb-0">{text}</p>
    </div>
  </div>
);
const InventoryCard = ({ icon, title }) => (
  <div className="col-lg-3 col-md-4 col-sm-6 mb-4">
    <div className="p-4 bg-white rounded-4 shadow-sm border text-center h-100 transition-all hover-shadow">
      <i
        className={`${icon} fs-1 mb-3 d-block`}
        style={{ color: DefaultData.THEMECOLOR1 }}
      ></i>
      <h6 className="fw-bold text-dark mb-0">{title}</h6>
    </div>
  </div>
);
const AIFeatureCard = ({ icon, title, text, badge }) => (
  <div className="col-lg-4 col-md-6 mb-4 text-start">
    <div
      className="p-4 bg-white rounded-5 shadow-sm border-top border-4 h-100"
      style={{ borderTopColor: DefaultData.THEMECOLOR1 }}
    >
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div
          className="rounded-4 d-flex align-items-center justify-content-center bg-light"
          style={{ width: "50px", height: "50px" }}
        >
          <i className={`${icon} fs-3 text-primary`}></i>
        </div>
        {badge && (
          <span
            className="badge bg-soft-primary text-primary rounded-pill px-2 py-1 small"
            style={{ backgroundColor: "#e7f0ff" }}
          >
            {badge}
          </span>
        )}
      </div>
      <h5 className="fw-bold mb-2">{title}</h5>
      <p className="text-muted small mb-0" style={{ lineHeight: "1.6" }}>
        {text}
      </p>
    </div>
  </div>
);

export default function VisaProviders() {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [basicValues, setBasicValues] = useState({
    companyName: "",
    email: "",
    phoneNumber: "",
    password: "",
    planType: "TRIAL",
    companyType: "VISA",
    userType: "VISAUSER",
    country: "IN",
    currency: "INR",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (currencyFlags && Array.isArray(currencyFlags)) {
      const sortedCountries = [...currencyFlags].sort((a, b) =>
        a.country.localeCompare(b.country)
      );
      setCountries(sortedCountries);
    }
  }, []);

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    if (name === "country") {
      const selectedCountry = countries.find((c) => c.countryCode === value);
      setBasicValues({
        ...basicValues,
        country: value,
        currency: selectedCountry?.code || "",
      });
    } else {
      setBasicValues({ ...basicValues, [name]: value });
    }
    if (errors[name] === false) setErrors({ ...errors, [name]: true });
  };

  const validateBasicForm = () => {
    let temp = {};
    temp.companyName = basicValues.companyName !== "";
    temp.email =
      basicValues.email !== "" && /\S+@\S+\.\S+/.test(basicValues.email);
    temp.phoneNumber = basicValues.phoneNumber !== "";
    temp.password =
      basicValues.password !== "" && basicValues.password.length >= 6;
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
  const applyErrorClass = (field) =>
    field in errors && errors[field] === false ? " is-invalid" : "";

  const handleBasicSubmit = async (e) => {
    e.preventDefault();
    if (!validateBasicForm()) return;
    setLoading(true);
    setErrors({});
    try {
      const requestData = {
        CompanyName: basicValues.companyName,
        Email: basicValues.email,
        PhoneNumber: basicValues.phoneNumber,
        Country: basicValues.country,
        Currency: basicValues.currency,
        Password: basicValues.password,
        CompanyType: basicValues.companyType,
        UserType: basicValues.userType,
        PlanType: basicValues.planType,
        ParentCompanyId: DefaultData.DEFAULTCOMPANYID,
        DomainUrl: window.location.origin,
      };
      const response = await apiService.create(
        config.REGISTERCOMPANY,
        requestData
      );
      if (response && response.success === true) {
        navigate("/registration-pending", {
          replace: true,
          state: {
            userEmail: basicValues.email,
            message: response.message,
            companyId: response.companyId,
          },
        });
      }
    } catch (error) {
      const errorData = error.response?.data;
      setErrors({
        submit: errorData?.message || "Registration failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={{ backgroundColor: "#ffffff" }}>
      {/* --- 1. HERO SECTION (Layout from base code logic + Design content) --- */}
      <header
        className="pb-1"
        style={{
          background: `linear-gradient(135deg, ${DefaultData.THEMECOLOR1} 0%, #0f172a 100%)`,
        }}
      >
        <div className="container  pb-4">
          <div className="row align-items-center g-5">
            {/* Content Left */}
            <div className="col-lg-7 text-lg-start text-center">
              <h1
                className="display-5 fw-bolder text-white"
                style={{ lineHeight: "1.1", marginTop:"-100px" }}
              >
                Stop Chasing Agents. <br />
                <span style={{ color: "#FF7F00" }}>Start Growing.</span>
              </h1>

              <p className="fs-5 mb-5 text-white-50">
                Join the world's fastest-growing B2B travel network. We connect
                local DMCs directly with 1000's of qualified travel agents globally.
              </p>

              <div className="row g-4">
                <div className="col-md-6">
                  <HeroFeature
                    icon="ri-team-line"
                    title="1000's of Active Agents"
                    text="Instant access to verified B2B travel buyers."
                  />
                  <HeroFeature
                    icon="ri-hotel-bed-line"
                    title="Promote Properties"
                    text="List hotels with live allotments and rates."
                  />
                  <HeroFeature
                    icon="ri-building-2-line"
                    title="DMC Brand Visibility"
                    text="Showcase your DMC to a global audience."
                  />
                </div>

                <div className="col-md-6">
                  <HeroFeature
                    icon="ri-plane-line"
                    title="Move Series Fares"
                    text="Sell fixed departures and group blocks faster."
                  />
                  <HeroFeature
                    icon="ri-flashlight-line"
                    title="Live Hot Deals"
                    text="Push limited-time offers instantly."
                  />
                  <HeroFeature
                    icon="ri-robot-line"
                    title="AI-Powered Packages"
                    text="Create itineraries and pricing using AI."
                  />
                </div>
              </div>
            </div>

            <div className="col-lg-5 d-flex justify-content-center justify-content-lg-end">
              <div
                className="card shadow-lg border-0"
                style={{
                  borderRadius: "1rem",
                  width: "100%",
                  maxWidth: "500px",
                  marginTop: "10px",
                }}
              >
                <div className="card-body p-lg-5 p-4">
                  <h4 className="fw-bold text-center">
                    Visa Provider Registration
                  </h4>
                  <p className="text-muted text-center mb-4">
                    Join our network of travel providers
                  </p>
                  <form
                    onSubmit={handleBasicSubmit}
                    autoComplete="off"
                    noValidate
                  >
                    <div className="mb-3">
                      <label className="form-label">Company Name*</label>
                      <input
                        type="text"
                        className={`form-control ${applyErrorClass(
                          "companyName"
                        )}`}
                        name="companyName"
                        value={basicValues.companyName}
                        onChange={handleBasicChange}
                        placeholder="Enter Company Name"
                        required
                      />
                      {errors.companyName === false && (
                        <div className="invalid-feedback d-block">
                          Please enter company name.
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Email*</label>
                      <input
                        type="text"
                        className={`form-control ${applyErrorClass("email")}`}
                        name="email"
                        value={basicValues.email}
                        onChange={handleBasicChange}
                        placeholder="Enter Email"
                        required
                      />
                      {errors.email === false && (
                        <div className="invalid-feedback d-block">
                          Please enter a valid email.
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Phone Number*</label>
                      <input
                        type="text"
                        className={`form-control ${applyErrorClass(
                          "phoneNumber"
                        )}`}
                        name="phoneNumber"
                        value={basicValues.phoneNumber}
                        onChange={handleBasicChange}
                        placeholder="Enter Phone Number"
                        maxLength={15}
                        required
                      />
                      {errors.phoneNumber === false && (
                        <div className="invalid-feedback d-block">
                          Please enter phone number.
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Country</label>
                      <select
                        className={`form-select ${applyErrorClass("country")}`}
                        name="country"
                        value={basicValues.country}
                        onChange={handleBasicChange}
                        required
                      >
                        {countries.map((country) => (
                          <option
                            key={country.countryCode}
                            value={country.countryCode}
                          >
                            {country.country}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Password*</label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className={`form-control pe-5 ${applyErrorClass(
                            "password"
                          )}`}
                          name="password"
                          value={basicValues.password}
                          onChange={handleBasicChange}
                          placeholder="Min. 6 characters"
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-decoration-none text-muted"
                          onClick={togglePasswordVisibility}
                          style={{ right: "0.5rem" }}
                        >
                          <i
                            className={`ri-eye-${
                              showPassword ? "line" : "off-line"
                            } fs-5`}
                          ></i>
                        </button>
                      </div>
                      {errors.password === false && (
                        <div className="invalid-feedback d-block">
                          Password must be at least 6 characters.
                        </div>
                      )}
                    </div>
                    {errors.submit && (
                      <div className="alert alert-danger mt-3">
                        {errors.submit}
                      </div>
                    )}
                    <div className="mt-4">
  <button
    type="submit"
    className="btn w-100"
    style={{
      backgroundColor: DefaultData.THEMECOLOR1,
      color: DefaultData.THEMECOLOR3,
    }}
    disabled={loading}
  >
    {loading
      ? "Registering..."
      : "REGISTER FOR 30 DAYS FREE TRIAL"}
  </button>
</div>

                    <div className="mt-3 text-center">
                      <p className="mb-0 text-muted">
                        Already a partner?{" "}
                        <Link
                          to="/"
                          className="fw-semibold text-decoration-underline"
                          style={{ color: DefaultData.THEMECOLOR2 }}
                        >
                          Sign In
                        </Link>
                      </p>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* --- 2. BRAND PROMOTION SECTION --- */}
      <section className="pt-2 pb-4 bg-white">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <img
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=800&q=80"
                alt="DMC Promotion"
                className="img-fluid rounded-5 shadow-lg"
              />
            </div>
            <div className="col-lg-6">
              <span className="badge bg-info text-white px-3 py-2 mb-3">
                Brand Visibility
              </span>
              <h2 className="display-7 fw-bold mb-4 text-start">
                Promote Your DMC to <br />
                1000's Verified Agents
              </h2>
              <p className="text-muted fs-5 mb-4 text-start">
                Don't just sell products—sell your expertise. We put your DMC
                brand in front of a global network of travel professionals
                looking for trusted destination partners.
              </p>
              <CheckItem
                title="Verified Supplier Status"
                text="Get a 'Verified' badge that builds instant trust with global agents."
              />
              <CheckItem
                title="Global DMC Directory"
                text="Be discovered by agents searching for experts in your specific regions."
              />
              <CheckItem
                title="Direct Branding"
                text="Showcase your company logo, profile, and unique selling points."
              />
            </div>
          </div>
        </div>
      </section>

      <section className="pt-2 bg-white text-start">
        <div className="container-fluid px-lg-5 px-3">
          <div className="row g-4 align-items-stretch">
            {/* --- SERIES FARES --- */}
            <div className="col-lg-4 col-md-6">
              <div className="h-100 p-4 rounded-5 shadow-sm border bg-light">
                <img
                  src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=800&q=80"
                  alt="Series Fares"
                  className="img-fluid rounded-4 mb-4"
                />

                <span className="badge bg-warning text-dark px-3 py-2 mb-3">
                  Revenue Booster
                </span>

                <h5 className="fw-bold mb-3">
                  Liquidate Series Inventory Faster
                </h5>

                <p className="text-muted fs-6 mb-4">
                  Reach agents actively searching for guaranteed group seats.
                </p>

                <CheckItem
                  title="Fixed Departures"
                  text="Showcase pre-purchased blocks."
                />
                <CheckItem
                  title="Guaranteed Pricing"
                  text="Lock fares in advance."
                />
                <CheckItem
                  title="Bulk Sales"
                  text="Sell to MICE-focused agencies."
                />
              </div>
            </div>

            {/* --- HOTEL PROPERTIES --- */}
            <div className="col-lg-4 col-md-6">
              <div className="h-100 p-4 rounded-5 shadow-sm border bg-light">
                <img
                  src="https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=800&q=80"
                  alt="Hotel Properties"
                  className="img-fluid rounded-4 mb-4"
                />

                <span className="badge bg-primary text-white px-3 py-2 mb-3">
                  Property Management
                </span>

                <h5 className="fw-bold mb-3">Maximize Your Property Yield</h5>

                <p className="text-muted fs-6 mb-4">
                  Sell directly to verified agents without OTA commissions.
                </p>

                <CheckItem
                  title="Direct Contracting"
                  text="Secure B2B net rates."
                />
                <CheckItem title="Live Allotments" text="Avoid overbooking." />
                <CheckItem
                  title="Rich Media"
                  text="Increase conversions with visuals."
                />
              </div>
            </div>

            {/* --- SCALE YOUR DMC --- */}
            <div className="col-lg-4 col-md-12">
              <div className="h-100 p-4 rounded-5 shadow-sm border bg-light">
                <img
                  src="https://img.freepik.com/free-photo/concept-travel-with-accessories-baggage_23-2149153258.jpg"
                  alt="Scale DMC"
                  className="img-fluid rounded-4 mb-4"
                />

                <span className="badge bg-success text-white px-3 py-2 mb-3">
                  Scale Faster
                </span>

                <h5 className="fw-bold mb-3">
                  Everything a DMC Needs to Scale
                </h5>

                <p className="text-muted fs-6 mb-4">
                  Tools built to help you grow faster with less effort.
                </p>

                <CheckItem
                  title="Bulk Uploads"
                  text="Import packages in minutes."
                />
                <CheckItem
                  title="Dynamic Pricing"
                  text="Season & agent-based pricing."
                />
                <CheckItem
                  title="White-Label Quotes"
                  text="Professional PDFs instantly."
                />
                <CheckItem
                  title="Inventory Control"
                  text="Hotels, transport & services."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- 4. SERIES FARES SECTION --- */}
      <section className="pt-2 bg-white">
        <div className="container py-lg-5 text-center">
          <h2 className="display-5 fw-bold mb-2">
            Global Inventory Distribution
          </h2>
          <p className="text-muted mb-5 fs-5">
            One centralized dashboard to manage and sell all your travel
            services.
          </p>
          <div className="row g-4">
            <InventoryCard icon="ri-plane-line" title="Series Fares" />
            <InventoryCard icon="ri-suitcase-2-line" title="Holiday Packages" />
            <InventoryCard icon="ri-hotel-line" title="Hotel Properties" />
            <InventoryCard icon="ri-map-pin-user-line" title="Sightseeing" />
            <InventoryCard icon="ri-bus-2-line" title="Transfers" />
            <InventoryCard icon="ri-passport-line" title="Visa Services" />
            <InventoryCard icon="ri-group-line" title="Group Tours" />
            <InventoryCard icon="ri-headphone-line" title="FIT Packages" />
          </div>
        </div>
      </section>
      <section className=" bg-white">
        <div className="container py-lg-5 text-center">
          <h2 className="display-5 fw-bold mb-5">
            AI-Powered DMC Intelligence
          </h2>
          <div className="row">
            <AIFeatureCard
              icon="ri-file-list-3-line"
              badge="Core"
              title="AI Content Extraction"
              text="Upload PDFs/Images. AI extracts itinerary days, hotels, and activities into searchable data."
            />
            <AIFeatureCard
              icon="ri-coin-line"
              badge="Revenue"
              title="AI Smart Pricing"
              text="Considers seasonality, demand, and agent history to suggest max-margin sell prices."
            />
            <AIFeatureCard
              icon="ri-user-search-line"
              badge="Leads"
              title="AI Agent Matching"
              text="AI lead scoring routes high-intent B2B agents directly to your sales team."
            />
            <AIFeatureCard
              icon="ri-chat-quote-line"
              badge="Quotes"
              title="AI Quotation Assistant"
              text="Build branded PDF quotes in minutes. AI suggests hotels and tiers based on agent budget."
            />

            <AIFeatureCard
              icon="ri-robot-line"
              badge="Support"
              title="AI Support Bot"
              text="Handles 24/7 agent queries regarding visa requirements and package inclusions."
            />

            <AIFeatureCard
              icon="ri-bar-chart-grouped-line"
              badge="Growth"
              title="Demand Dashboard"
              text="Visualize trending destinations and agent price sensitivity in real-time."
            />
          </div>
        </div>
      </section>

      <PricingSection 
                buttonText="Start Free Trial" 
                onPlanClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} 
            />

      {/* --- 6. COMPARISON SECTION --- */}
      <section className="py-5 bg-dark text-white">
        <div className="container py-5 text-center">
          <div className="mb-5">
            <h2 className="display-4 fw-bold" style={{ color: "#ffff" }}>
              Traditional Way vs. AI Portal
            </h2>
            <p className="text-white-50 fs-5" style={{ color: "#ffff" }}>
              Why leading DMCs are switching to our technology.
            </p>
          </div>
          <div className="row g-4 text-start">
            <div className="col-md-6">
              <div
                className="p-5 rounded-5 border border-secondary h-100"
                style={{ backgroundColor: "rgba(255,255,255,0.05)" }}
              >
                <h4 className="mb-4 text-danger fw-bold">
                  <i className="ri-close-circle-line me-2"></i> The Old Way
                </h4>
                <ul className="list-unstyled opacity-75">
                  <li className="mb-3">❌ Sending thousands of spam emails</li>
                  <li className="mb-3">❌ Manual typing and PDF creation.</li>
                  <li className="mb-3">
                    ❌ Manual allotment checks via phone/excel
                  </li>
                  <li className="mb-3">
                    ❌ Losing money on unsold series seats
                  </li>
                  <li className="mb-3">
                    ❌ WhatsApp inquiries 24/7 for quotes
                  </li>
                </ul>
              </div>
            </div>
            <div className="col-md-6">
              <div
                className="p-5 rounded-5 border border-primary h-100"
                style={{ backgroundColor: "rgba(13,110,253,0.1)" }}
              >
                <h4 className="mb-4 text-primary fw-bold">
                  <i className="ri-checkbox-circle-line me-2"></i> Our Portal
                  Way
                </h4>
                <ul className="list-unstyled">
                  <li className="mb-3">
                    ✅ Your inventory is LIVE for 1000's agents 24/7
                  </li>
                  <li className="mb-3">✅ Automated document-to-package.</li>
                  <li className="mb-3">
                    ✅ Automatic allotment management system
                  </li>
                  <li className="mb-3">
                    ✅ Sell every Series seat via global distribution
                  </li>
                  <li className="mb-3">
                    ✅ Agents download PDF quotes directly
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- 7. HOW IT WORKS --- */}
      <section className="py-5 bg-white border-top">
        <div className="container py-5 text-center">
          <div className="mb-5">
            <h2 className="display-6 fw-bold">How it Works for DMCs</h2>
            <p className="text-muted">
              Four simple steps to grow your business
            </p>
          </div>
          <div className="row g-4">
            <StepCard
              number="1"
              title="Register"
              text="Create your account and complete your company profile."
            />
            <StepCard
              number="2"
              title="Upload"
              text="Add your Series Fares, Hotels, and Packages."
            />
            <StepCard
              number="3"
              title="Promote"
              text="Your inventory is visible to 1000's agents instantly."
            />
            <StepCard
              number="4"
              title="Earn"
              text="Receive direct leads and grow your B2B revenue."
            />
          </div>
        </div>
      </section>
      <section className="py-5 bg-light text-start border-top">
        <div className="container py-lg-5">
          <h2 className="display-6 fw-bold text-center mb-5">
            Frequently Asked Questions
          </h2>

          <div className="row g-4">
            <div className="col-md-6">
              <h6 className="fw-bold">Is this platform only for large DMCs?</h6>
              <p className="text-muted small">
                No. Small and mid-sized DMCs benefit the most by reaching global
                agents instantly.
              </p>
            </div>

            <div className="col-md-6">
              <h6 className="fw-bold">How fast can I start selling?</h6>
              <p className="text-muted small">
                Most DMCs go live within 30 minutes after registration and
                approval.
              </p>
            </div>

            <div className="col-md-6">
              <h6 className="fw-bold">Do agents see my net rates?</h6>
              <p className="text-muted small">
                No. Only the prices you choose to publish are visible to agents.
                Your margins are protected.
              </p>
            </div>

            <div className="col-md-6">
              <h6 className="fw-bold">Can I promote Series Fares?</h6>
              <p className="text-muted small">
                Yes. You can list fixed departures and group fares to sell
                inventory faster.
              </p>
            </div>

            <div className="col-md-6">
              <h6 className="fw-bold">Can I list my hotel properties?</h6>
              <p className="text-muted small">
                Yes. You can manage live allotments, pricing, and property
                images in one place.
              </p>
            </div>

            <div className="col-md-6">
              <h6 className="fw-bold">Is AI mandatory to use?</h6>
              <p className="text-muted small">
                No. AI is optional. You can create and manage products manually
                if you prefer.
              </p>
            </div>

            <div className="col-md-6">
              <h6 className="fw-bold">How does AI help me?</h6>
              <p className="text-muted small">
                AI helps you create itineraries, descriptions, and pricing
                faster with less effort.
              </p>
            </div>

            <div className="col-md-6">
              <h6 className="fw-bold">Is this platform secure?</h6>
              <p className="text-muted small">
                Yes. Only verified agents can access your inventory, and all
                data is protected.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
